import {
  Component,
  DestroyRef,
  Input,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreadCrumb } from '../../core/types';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { EMPTY, Observable, filter, merge, of } from 'rxjs';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterLink,
} from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe, NgClass } from '@angular/common';
import { personalTabFor } from '../personal-job-search-frame/personal-job-search-frame.component';

/**
 * «Meine Bewerbungen» and the Bewerbungsdossier start their content at the
 * left edge of the page, so the crumbs there sit flush instead of indented.
 * So do the applicants page of a posting and the received application.
 */
export function breadcrumbFlushWithContent(url: string): boolean {
  const key = personalTabFor(url)?.key;
  return (
    key === 'applications' ||
    key === 'documents' ||
    /^\/my-schools\/[^/]+\/jobs\/[^/]+\/applicants\/?$/.test(
      url.split(/[?#;]/)[0]
    ) ||
    /^\/received-applications\/[^/]+\/?$/.test(url.split(/[?#;]/)[0])
  );
}

/** Settings pages whose content is the centred column (col 4 to 9 of 12). */
const SETTINGS_COLUMN =
  /^\/settings\/(marketing|privacy|change-email|change-name(\/info)?|change-password(\/secure-password)?|delete-account)\/?$/;

/**
 * Where the crumbs start on desktop, so they line up with the page title.
 * Each value mirrors the grid of those pages: `column` the centred settings
 * column, `wide` Konto and Abo (col 3 to 12 from lg), `contact` Personalien
 * (col 3 to 10), `detail` a Bewerbung of the Fachkraft (col 3 to 10 from
 * lg), `templates` the template page, `flush` pages without own indent, else
 * `indent`.
 */
export type BreadcrumbAlign =
  | 'indent'
  | 'flush'
  | 'column'
  | 'wide'
  | 'contact'
  | 'detail'
  | 'templates';

export function breadcrumbAlignFor(url: string): BreadcrumbAlign {
  const path = url.split(/[?#;]/)[0];
  if (SETTINGS_COLUMN.test(path)) return 'column';
  if (/^\/settings\/(account|subscription)\/?$/.test(path)) return 'wide';
  if (/^\/settings\/change-contact\/?$/.test(path)) return 'contact';
  if (/^\/settings\/templates\/?$/.test(path)) return 'templates';
  if (/^\/applications\/[0-9a-f-]{36}\/?$/.test(path)) return 'detail';
  return breadcrumbFlushWithContent(url) ? 'flush' : 'indent';
}

/**
 * Legacy layout breadcrumb strip. Hidden on public Schulprofil (`/schools/:id`) because that
 * screen uses `app-school-authority-breadcrumb` in-page; clearing the service alone is not
 * enough (router `firstChild` vs leaf timing).
 */
@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  imports: [RouterLink, MatIcon, AsyncPipe, NgClass],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class BreadcrumbsComponent {
  @Input() crumbs$: Observable<BreadCrumb[]> = EMPTY;
  renderElement = false;

  /** When false, do not render the layout strip (e.g. public `/schools/:id`). */
  readonly legacyStripAllowed = signal(true);
  readonly navigating = signal(false);
  readonly align = signal<BreadcrumbAlign>('indent');

  private readonly router = inject(Router);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.crumbs$ = this.breadcrumbService.breadcrumbs$;
    this.crumbs$.subscribe(crumbs => {
      this.renderElement = crumbs.length > 0;
    });

    const syncPublicSchoolChrome = () => {
      const path = this.router.url.split('?')[0];
      const hide = this.isPublicSchoolProfilePath(path);
      this.align.set(breadcrumbAlignFor(path));
      this.legacyStripAllowed.set(!hide);
      if (hide) {
        this.breadcrumbService.clear();
      }
    };

    merge(
      of(null),
      this.router.events.pipe(
        filter(
          (e): e is NavigationStart | NavigationEnd =>
            e instanceof NavigationStart || e instanceof NavigationEnd
        )
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(e => {
        if (e instanceof NavigationStart) {
          this.navigating.set(true);
        }
        if (e instanceof NavigationEnd) {
          this.navigating.set(false);
        }
        syncPublicSchoolChrome();
      });
  }

  /** Exactly `/schools/:id` (öffentliches Profil), not list, subscription, edit, onboarding. */
  private isPublicSchoolProfilePath(path: string): boolean {
    const m = path.match(/^\/schools\/([^/]+)$/);
    if (!m) return false;
    return m[1] !== 'subscription';
  }

  showSeperator(index: number, crumbs: BreadCrumb[]) {
    const l = crumbs.length;
    return l > 1 && (index === 0 || index < l - 1);
  }
}
